import * as React from "react";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface TimePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// Hours available for selection (evening pickup hours)
const hours = ["20", "21", "22", "23"];

// Minutes in 5-minute intervals
const minutes = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

export const TimePicker = React.forwardRef<HTMLInputElement, TimePickerProps>(
  ({ value, onChange, placeholder = "Selecteer tijd", disabled, className, ...props }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [selectedTime, setSelectedTime] = React.useState(() => {
      if (value) return value;
      return "21:30"; // Default to 21:30 (evening pickup)
    });

    const hourScrollRef = React.useRef<HTMLDivElement>(null);
    const minuteScrollRef = React.useRef<HTMLDivElement>(null);
    const hourScrollTimeoutRef = React.useRef<number | null>(null);
    const minuteScrollTimeoutRef = React.useRef<number | null>(null);

    React.useEffect(() => {
      if (value) {
        setSelectedTime(value);
      }
    }, [value]);

    // Auto-scroll to selected value when popover opens
    React.useEffect(() => {
      if (open && selectedTime) {
        const [selectedHour, selectedMinute] = selectedTime.split(":");
        
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          // Scroll to selected hour
          if (hourScrollRef.current) {
            const hourElements = hourScrollRef.current.querySelectorAll('[data-hour]');
            const selectedHourElement = Array.from(hourElements).find(
              (el) => el.getAttribute('data-hour') === selectedHour
            ) as HTMLElement;
            
            if (selectedHourElement) {
              selectedHourElement.scrollIntoView({ 
                behavior: 'instant', 
                block: 'center',
                inline: 'center'
              });
            }
          }
          
          // Scroll to selected minute
          if (minuteScrollRef.current) {
            const minuteElements = minuteScrollRef.current.querySelectorAll('[data-minute]');
            const selectedMinuteElement = Array.from(minuteElements).find(
              (el) => el.getAttribute('data-minute') === selectedMinute
            ) as HTMLElement;
            
            if (selectedMinuteElement) {
              selectedMinuteElement.scrollIntoView({ 
                behavior: 'instant', 
                block: 'center',
                inline: 'center'
              });
            }
          }
        }, 100);
      }
    }, [open, selectedTime]);

    const [selectedHour, selectedMinute] = selectedTime.split(":");

    const handleHourChange = (hour: string) => {
      const newTime = `${hour}:${selectedMinute}`;
      setSelectedTime(newTime);
      onChange?.(newTime);
    };

    const handleMinuteChange = (minute: string) => {
      const newTime = `${selectedHour}:${minute}`;
      setSelectedTime(newTime);
      onChange?.(newTime);
    };

    const handleHourScroll = () => {
      if (hourScrollTimeoutRef.current) {
        window.clearTimeout(hourScrollTimeoutRef.current);
      }
      hourScrollTimeoutRef.current = window.setTimeout(() => {
        const container = hourScrollRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;

        const items = Array.from(container.querySelectorAll('[data-hour]')) as HTMLElement[];

        let closest: HTMLElement | null = null;
        let minDist = Infinity;

        for (const el of items) {
          const r = el.getBoundingClientRect();
          const c = r.top + r.height / 2;
          const d = Math.abs(c - containerCenter);
          if (d < minDist) {
            minDist = d;
            closest = el;
          }
        }

        const value = closest?.getAttribute('data-hour');
        if (value && value !== selectedHour) {
          handleHourChange(value);
        }
      }, 100);
    };

    const handleMinuteScroll = () => {
      if (minuteScrollTimeoutRef.current) {
        window.clearTimeout(minuteScrollTimeoutRef.current);
      }
      minuteScrollTimeoutRef.current = window.setTimeout(() => {
        const container = minuteScrollRef.current;
        if (!container) return;

        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;

        const items = Array.from(container.querySelectorAll('[data-minute]')) as HTMLElement[];

        let closest: HTMLElement | null = null;
        let minDist = Infinity;

        for (const el of items) {
          const r = el.getBoundingClientRect();
          const c = r.top + r.height / 2;
          const d = Math.abs(c - containerCenter);
          if (d < minDist) {
            minDist = d;
            closest = el;
          }
        }

        const value = closest?.getAttribute('data-minute');
        if (value && value !== selectedMinute) {
          handleMinuteChange(value);
        }
      }, 100);
    };

    const handleHourWheel = (e: React.WheelEvent) => {
      const direction = Math.sign(e.deltaY);
      const currentIndex = hours.indexOf(selectedHour);
      const nextIndex = Math.min(Math.max(currentIndex + direction, 0), hours.length - 1);
      
      if (nextIndex !== currentIndex) {
        const newHour = hours[nextIndex];
        handleHourChange(newHour);
      }
    };

    const handleMinuteWheel = (e: React.WheelEvent) => {
      const direction = Math.sign(e.deltaY);
      const currentIndex = minutes.indexOf(selectedMinute);
      const nextIndex = Math.min(Math.max(currentIndex + direction, 0), minutes.length - 1);
      
      if (nextIndex !== currentIndex) {
        const newMinute = minutes[nextIndex];
        handleMinuteChange(newMinute);
      }
    };

    const handleHourKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        
        const direction = e.key === 'ArrowDown' ? 1 : -1;
        const currentIndex = hours.indexOf(selectedHour);
        const nextIndex = Math.min(Math.max(currentIndex + direction, 0), hours.length - 1);
        
        if (nextIndex !== currentIndex) {
          const newHour = hours[nextIndex];
          handleHourChange(newHour);
        }
      }
    };

    const handleMinuteKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        
        const direction = e.key === 'ArrowDown' ? 1 : -1;
        const currentIndex = minutes.indexOf(selectedMinute);
        const nextIndex = Math.min(Math.max(currentIndex + direction, 0), minutes.length - 1);
        
        if (nextIndex !== currentIndex) {
          const newMinute = minutes[nextIndex];
          handleMinuteChange(newMinute);
        }
      }
    };

    const formatDisplayTime = (time: string) => {
      if (!time) return "";
      try {
        const [hours, minutes] = time.split(":");
        const hour24 = parseInt(hours);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const period = hour24 >= 12 ? "PM" : "AM";
        return `${time} (${hour12}:${minutes} ${period})`;
      } catch {
        return time;
      }
    };

    return (
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !selectedTime && "text-muted-foreground",
                className
              )}
              disabled={disabled}
            >
              <Clock className="mr-2 h-4 w-4" />
              {selectedTime ? formatDisplayTime(selectedTime) : placeholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <div className="p-6">
              {/* Large Time Display */}
              <div className="text-center mb-6">
                <div className="text-4xl font-light text-foreground tracking-wider">
                  {selectedTime}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {formatDisplayTime(selectedTime)}
                </div>
              </div>

              {/* iOS-style Wheel Selectors */}
              <div className="flex items-center justify-center gap-4">
                {/* Hours Wheel */}
                <div className="flex flex-col items-center">
                  <div className="text-xs text-muted-foreground mb-3 font-medium">Uur</div>
                  <div className="relative h-32 w-20 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent via-transparent to-background pointer-events-none z-10" />
                    <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-0 right-0 h-8 -translate-y-1/2 bg-primary/10 border border-primary/20 rounded-lg" />
                    <div 
                      ref={hourScrollRef}
                      onScroll={handleHourScroll}
                      onWheel={handleHourWheel}
                      onKeyDown={handleHourKeyDown}
                      tabIndex={0}
                      className="h-full overflow-y-auto overscroll-contain scrollbar-none snap-y snap-mandatory scroll-smooth focus:outline-none [-webkit-scrollbar:none] [scrollbar-width:none] [scrollbar-gutter:stable] [-webkit-appearance:none]"
                      style={{ 
                        WebkitOverflowScrolling: 'touch',
                        WebkitAppearance: 'none',
                        scrollbarWidth: 'none',
                        scrollbarGutter: 'stable',
                        msOverflowStyle: 'none',
                        overscrollBehavior: 'contain',
                        scrollSnapType: 'y mandatory'
                      }}
                    >
                      <div className="py-20">
                        {hours.map((hour) => (
                          <div
                            key={hour}
                            data-hour={hour}
                            style={{
                              scrollSnapAlign: 'center',
                              scrollSnapStop: 'always',
                            }}
                            className={cn(
                              "h-8 flex items-center justify-center cursor-pointer transition-all duration-150 snap-center text-lg select-none",
                              hour === selectedHour 
                                ? "text-primary font-semibold scale-125 drop-shadow-sm" 
                                : "text-muted-foreground hover:text-foreground hover:scale-105"
                            )}
                            onClick={() => handleHourChange(hour)}
                          >
                            {hour}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Separator */}
                <div className="text-2xl text-muted-foreground font-light self-center mb-6">:</div>

                {/* Minutes Wheel */}
                <div className="flex flex-col items-center">
                  <div className="text-xs text-muted-foreground mb-3 font-medium">Min</div>
                  <div className="relative h-32 w-20 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent via-transparent to-background pointer-events-none z-10" />
                    <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-0 right-0 h-8 -translate-y-1/2 bg-primary/10 border border-primary/20 rounded-lg" />
                    <div 
                      ref={minuteScrollRef}
                      onScroll={handleMinuteScroll}
                      onWheel={handleMinuteWheel}
                      onKeyDown={handleMinuteKeyDown}
                      tabIndex={0}
                      className="h-full overflow-y-auto overscroll-contain scrollbar-none snap-y snap-mandatory scroll-smooth focus:outline-none [-webkit-scrollbar:none] [scrollbar-width:none] [scrollbar-gutter:stable] [-webkit-appearance:none]"
                      style={{ 
                        WebkitOverflowScrolling: 'touch',
                        WebkitAppearance: 'none',
                        scrollbarWidth: 'none',
                        scrollbarGutter: 'stable',
                        msOverflowStyle: 'none',
                        overscrollBehavior: 'contain',
                        scrollSnapType: 'y mandatory'
                      }}
                    >
                      <div className="py-20">
                        {minutes.map((minute) => (
                          <div
                            key={minute}
                            data-minute={minute}
                            style={{
                              scrollSnapAlign: 'center',
                              scrollSnapStop: 'always',
                            }}
                            className={cn(
                              "h-8 flex items-center justify-center cursor-pointer transition-all duration-150 snap-center text-lg select-none",
                              minute === selectedMinute 
                                ? "text-primary font-semibold scale-125 drop-shadow-sm" 
                                : "text-muted-foreground hover:text-foreground hover:scale-105"
                            )}
                            onClick={() => handleMinuteChange(minute)}
                          >
                            {minute}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

TimePicker.displayName = "TimePicker";